import React, { useEffect, useMemo, useRef, useState } from 'react';
import { OUTER_RING_EMOTES, INNER_RING_EMOTES, EmoteData } from './emotes';
import { EMOTE_CATEGORIES } from './categories';
import { FAVORITE_SLOT_COUNT, loadFavorites, saveFavorites } from './favorites';
import './EmoteWheel.scss';

// The emote menu is a drawer: themes on the left, a grid of animated cards in the middle, a large
// preview on the right and the nine favorite slots below.
//
// What did NOT change, because the client depends on it (emoteService.ts):
//  - it opens on "skymp5-client:showEmoteWheel" and closes on "hideEmoteWheel";
//  - an emote is played by RELEASING the key that opened the menu while pointing at it
//    ("skymp5-client:confirmEmoteWheelSelection"), never by a mouse click: a click on this surface
//    is the same left click Skyrim reads as "attack", it drew the weapon and left players stuck in
//    combat. So the cards react to the pointer only, and a mouse down does nothing;
//  - keys 1 to 9 on a pointed emote bind it to that favorite slot ("emote-favorites:" message);
//  - the picked id goes out as "emote-wheel:<id>".

const PER_PAGE = 15;
const CLICK_GRACE_MS = 200;
// Leaving a card does not drop the selection at once: the pointer crosses small gaps between cards,
// and goes to the preview to reach a family's variants
const HOVER_RELEASE_MS = 140;

const FAVORITES_TAB = 'favoris';
const ALL_TAB = 'tout';
const OTHER_TAB = 'divers';

const ALL_SLOTS: EmoteData[] = [...OUTER_RING_EMOTES, ...INNER_RING_EMOTES];
// The playable id of an emote or of one variant of a family -> its data
const EMOTES_BY_ID = new Map<string, EmoteData>();
// ... and the slot (the family head) that holds it
const SLOT_BY_ID = new Map<string, EmoteData>();
for (const slot of ALL_SLOTS) {
  SLOT_BY_ID.set(slot.id, slot);
  if (slot.variants) {
    for (const v of slot.variants) {
      EMOTES_BY_ID.set(v.id, v);
      SLOT_BY_ID.set(v.id, slot);
    }
  } else {
    EMOTES_BY_ID.set(slot.id, slot);
  }
}

interface Tab {
  id: string;
  label: string;
}

const KNOWN_IDS = new Set(ALL_SLOTS.map((s) => s.id));
const CATEGORY_SLOTS = new Map<string, EmoteData[]>();
const placed = new Set<string>();
for (const def of EMOTE_CATEGORIES) {
  const slots = def.ids.filter((id) => KNOWN_IDS.has(id)).map((id) => SLOT_BY_ID.get(id) as EmoteData);
  slots.forEach((s) => placed.add(s.id));
  CATEGORY_SLOTS.set(def.id, slots);
}
CATEGORY_SLOTS.set(OTHER_TAB, ALL_SLOTS.filter((s) => !placed.has(s.id)));
CATEGORY_SLOTS.set(ALL_TAB, ALL_SLOTS);

const TABS: Tab[] = [
  { id: FAVORITES_TAB, label: 'Favoris' },
  { id: ALL_TAB, label: 'Tout' },
  ...EMOTE_CATEGORIES.map((c) => ({ id: c.id, label: c.label })),
  ...((CATEGORY_SLOTS.get(OTHER_TAB) ?? []).length > 0 ? [{ id: OTHER_TAB, label: 'Divers' }] : []),
];

// Remembered between two openings of the menu
let lastTab = '';

// Physical key codes for the top number row (e.code does not depend on the keyboard layout)
const FAVORITE_KEY_CODES = ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9'];

const imageOf = (e: EmoteData): string => require(`./img/${e.image}`).default;

// An <img> on a .gif always plays and loops, with no way to pause it. With a full page of cards on
// screen that is a lot of motion for emotes nobody looks at, so a .gif only plays while its card is
// pointed at or selected; otherwise a canvas shows its first frame. A still image renders as is.
const EmoteThumb: React.FC<{ src: string; alt: string; playing: boolean; size: number }> = ({ src, alt, playing, size }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isGif = typeof src === 'string' && src.endsWith('.gif');

  useEffect(() => {
    if (!isGif || playing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      // Same crop as the live <img> (object-fit: cover, top centre), so the swap never shifts it
      const scale = Math.max(canvas.width / img.naturalWidth, canvas.height / img.naturalHeight);
      const w = img.naturalWidth * scale;
      const h = img.naturalHeight * scale;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, (canvas.width - w) / 2, 0, w, h);
    };
    img.src = src;
    return () => { cancelled = true; };
  }, [src, playing, isGif, size]);

  if (!isGif || playing) return <img src={src} alt={alt} />;
  return <canvas ref={canvasRef} aria-label={alt} />;
};

// Mirrors the favorites to the client so a bound slot plays on its hotkey without the menu ever
// opening (the client cannot read this browser's localStorage)
function pushFavoritesToClient(favorites: (string | null)[]): void {
  if ((window as any).skyrimPlatform?.sendMessage) {
    (window as any).skyrimPlatform.sendMessage('emote-favorites:' + JSON.stringify(favorites));
  }
}

const computeScale = (): number => Math.min(3, Math.max(1, Math.min(window.innerWidth / 1920, window.innerHeight / 1080)));

const EmoteWheel: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [tab, setTab] = useState(ALL_TAB);
  const [page, setPage] = useState(0);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<(string | null)[]>(() => new Array(FAVORITE_SLOT_COUNT).fill(null));
  const [justSetSlot, setJustSetSlot] = useState<number | null>(null);
  const [scale, setScale] = useState(computeScale);
  const openedAtRef = useRef(0);
  const releaseTimerRef = useRef<number | null>(null);

  useEffect(() => {
    const onResize = () => setScale(computeScale());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Pushed once on mount: favorites must work from the start of a session, not only after the
  // menu has been opened once
  useEffect(() => {
    pushFavoritesToClient(loadFavorites());
  }, []);

  const cancelRelease = () => {
    if (releaseTimerRef.current !== null) {
      window.clearTimeout(releaseTimerRef.current);
      releaseTimerRef.current = null;
    }
  };
  const scheduleRelease = () => {
    cancelRelease();
    releaseTimerRef.current = window.setTimeout(() => setHoveredId(null), HOVER_RELEASE_MS);
  };
  useEffect(() => cancelRelease, []);

  useEffect(() => {
    const showHandler = () => {
      const favs = loadFavorites();
      setFavorites(favs);
      setTab(lastTab && TABS.some((t) => t.id === lastTab) ? lastTab : favs.some(Boolean) ? FAVORITES_TAB : ALL_TAB);
      setPage(0);
      setHoveredId(null);
      setVisible(true);
      openedAtRef.current = Date.now();
    };
    const hideHandler = () => setVisible(false);
    window.addEventListener('skymp5-client:showEmoteWheel', showHandler);
    window.addEventListener('skymp5-client:hideEmoteWheel', hideHandler);
    return () => {
      window.removeEventListener('skymp5-client:showEmoteWheel', showHandler);
      window.removeEventListener('skymp5-client:hideEmoteWheel', hideHandler);
    };
  }, []);

  const items = useMemo<EmoteData[]>(() => {
    if (tab === FAVORITES_TAB) {
      const seen = new Set<string>();
      const out: EmoteData[] = [];
      for (const id of favorites) {
        if (!id || seen.has(id)) continue;
        const e = EMOTES_BY_ID.get(id);
        if (e) {
          seen.add(id);
          out.push(e);
        }
      }
      return out;
    }
    return CATEGORY_SLOTS.get(tab) ?? [];
  }, [tab, favorites]);

  const pageCount = Math.max(1, Math.ceil(items.length / PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pageItems = items.slice(safePage * PER_PAGE, safePage * PER_PAGE + PER_PAGE);

  const selectTab = (id: string) => {
    if (id === tab) return;
    lastTab = id;
    setTab(id);
    setPage(0);
    setHoveredId(null);
  };

  // The pointed emote (a playable id: a plain emote, or one variant of a family)
  const pointed = hoveredId ? EMOTES_BY_ID.get(hoveredId) ?? SLOT_BY_ID.get(hoveredId) ?? null : null;
  const pointedSlot = hoveredId ? SLOT_BY_ID.get(hoveredId) ?? null : null;

  // Keys, wheel and the pick itself only matter while the menu is open and has the input focus
  useEffect(() => {
    if (!visible) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const slot = FAVORITE_KEY_CODES.indexOf(e.code);
      if (slot !== -1 && hoveredId) {
        e.preventDefault();
        setFavorites((prev) => {
          const next = [...prev];
          next[slot] = hoveredId;
          saveFavorites(next);
          pushFavoritesToClient(next);
          return next;
        });
        setJustSetSlot(slot);
        window.setTimeout(() => setJustSetSlot((s) => (s === slot ? null : s)), 600);
        return;
      }
      const i = TABS.findIndex((t) => t.id === tab);
      if (e.code === 'ArrowDown') { e.preventDefault(); selectTab(TABS[Math.min(TABS.length - 1, i + 1)].id); }
      else if (e.code === 'ArrowUp') { e.preventDefault(); selectTab(TABS[Math.max(0, i - 1)].id); }
      else if (e.code === 'ArrowRight') { e.preventDefault(); setPage((p) => Math.min(pageCount - 1, p + 1)); setHoveredId(null); }
      else if (e.code === 'ArrowLeft') { e.preventDefault(); setPage((p) => Math.max(0, p - 1)); setHoveredId(null); }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setPage((p) => Math.max(0, Math.min(pageCount - 1, p + (e.deltaY > 0 ? 1 : -1))));
      setHoveredId(null);
    };

    // Sent by emoteService.ts when the player releases the key that opened the menu
    const onConfirm = () => {
      if (!hoveredId) return;
      if (Date.now() - openedAtRef.current < CLICK_GRACE_MS) return;
      if ((window as any).skyrimPlatform?.sendMessage) {
        (window as any).skyrimPlatform.sendMessage('emote-wheel:' + hoveredId);
      }
      setVisible(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('skymp5-client:confirmEmoteWheelSelection', onConfirm);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('skymp5-client:confirmEmoteWheelSelection', onConfirm);
    };
  }, [visible, hoveredId, pageCount, tab]);

  if (!visible) return null;

  const tabIndex = Math.max(0, TABS.findIndex((t) => t.id === tab));
  const countOf = (id: string): number => (id === FAVORITES_TAB ? favorites.filter(Boolean).length : (CATEGORY_SLOTS.get(id) ?? []).length);

  const point = (id: string) => {
    cancelRelease();
    setHoveredId(id);
  };

  return (
    // A mouse down anywhere does nothing, on purpose (see the header comment): preventDefault, so
    // there is no pressed state, focus change or native menu either
    <div className="emote-drawer" onMouseDown={(e) => e.preventDefault()}>
      <div className="emote-drawer__stack" style={{ transform: `scale(${scale})` }}>
        <div className="emote-drawer__panel">
          <nav className="emote-drawer__tabs" aria-label="Thèmes">
            <span className="emote-drawer__tab-glow" style={{ transform: `translateY(${tabIndex * 50}px)` }} aria-hidden="true" />
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`emote-drawer__tab${t.id === tab ? ' is-active' : ''}`}
                onMouseEnter={() => selectTab(t.id)}
              >
                <span className="emote-drawer__tab-label">{t.label}</span>
                <span className="emote-drawer__tab-count">{countOf(t.id)}</span>
              </button>
            ))}
          </nav>

          <section className="emote-drawer__main">
            <header className="emote-drawer__head">
              <h2 className="emote-drawer__title">{TABS[tabIndex].label}</h2>
              {pageCount > 1 && (
                <div className="emote-drawer__pager" aria-label={`Page ${safePage + 1} sur ${pageCount}`}>
                  {Array.from({ length: pageCount }, (_, i) => (
                    <span key={i} className={`emote-drawer__page${i === safePage ? ' is-on' : ''}`} />
                  ))}
                </div>
              )}
            </header>

            {items.length === 0 ? (
              <div className="emote-drawer__empty">
                <p>Aucun favori pour l'instant.</p>
                <p className="emote-drawer__empty-sub">Pointe une émote et appuie sur une touche de 1 à 9 pour la fixer ici.</p>
              </div>
            ) : (
              <ul className="emote-drawer__grid" key={`${tab}-${safePage}`}>
                {pageItems.map((emote, n) => {
                  const active = hoveredId !== null && (hoveredId === emote.id || pointedSlot?.id === emote.id);
                  return (
                    <li key={emote.id} style={{ ['--i' as any]: n }}>
                      <button
                        type="button"
                        className={`emote-drawer__card${active ? ' is-active' : ''}`}
                        onMouseEnter={() => point(emote.id)}
                        onMouseLeave={scheduleRelease}
                        title={emote.label}
                      >
                        <span className="emote-drawer__thumb">
                          <EmoteThumb src={imageOf(emote)} alt={emote.label} playing={active} size={140} />
                        </span>
                        <span className="emote-drawer__name">{emote.label}</span>
                        {emote.variants && <span className="emote-drawer__family">{emote.variants.length}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <aside className="emote-drawer__preview" onMouseEnter={cancelRelease} onMouseLeave={scheduleRelease}>
            {pointed && pointedSlot ? (
              <div className="emote-drawer__preview-body" key={pointed.id}>
                <div className="emote-drawer__stage">
                  <EmoteThumb src={imageOf(pointed)} alt={pointed.label} playing size={320} />
                </div>
                <h3 className="emote-drawer__preview-title">{pointed.label}</h3>
                {pointedSlot.variants && (
                  <div className="emote-drawer__variants">
                    {pointedSlot.variants.map((v, i) => (
                      <button
                        key={v.id}
                        type="button"
                        className={`emote-drawer__variant${hoveredId === v.id ? ' is-on' : ''}`}
                        onMouseEnter={() => point(v.id)}
                        title={v.label}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
                <p className="emote-drawer__hint"><kbd>relâche</kbd> la touche d'ouverture pour jouer</p>
                <p className="emote-drawer__hint"><kbd>1</kbd> à <kbd>9</kbd> pour la fixer en favori</p>
              </div>
            ) : (
              <div className="emote-drawer__preview-idle">
                <p className="emote-drawer__idle-title">Choisis une émote</p>
                <p className="emote-drawer__hint">Pointe une carte, puis relâche la touche d'ouverture pour la jouer.</p>
                <p className="emote-drawer__hint">Molette ou flèches gauche et droite : changer de page. Flèches haut et bas : changer de thème.</p>
              </div>
            )}
          </aside>
        </div>

        <div className="emote-drawer__favorites" aria-label="Favoris">
          {favorites.map((id, i) => {
            const e = id ? EMOTES_BY_ID.get(id) ?? null : null;
            const active = !!e && hoveredId === e.id;
            return (
              <button
                key={i}
                type="button"
                disabled={!e}
                className={`emote-drawer__fav${e ? '' : ' is-empty'}${justSetSlot === i ? ' is-set' : ''}${active ? ' is-active' : ''}`}
                onMouseEnter={e ? () => point(e.id) : undefined}
                onMouseLeave={e ? scheduleRelease : undefined}
                title={e ? e.label : `Pointe une émote et appuie sur ${i + 1} pour la fixer ici`}
              >
                <span className="emote-drawer__fav-num">{i + 1}</span>
                {e && <EmoteThumb src={imageOf(e)} alt={e.label} playing={active} size={56} />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EmoteWheel;
